import type { CSSProperties, ReactNode } from 'react';

import { parseAttachmentMarker } from '@shiftagent/shared';

import { MarkdownContent } from './MarkdownContent';
import { shortClockTime } from './format-time';

type Role = 'user' | 'assistant' | 'tool';

export type BubbleGroupPosition = 'solo' | 'first' | 'middle' | 'last';

interface Props {
  role: Role;
  timestamp?: string | undefined;
  children?: ReactNode;
  /**
   * Raw text content. Markdown-rendered for assistant; line-preserving
   * plain text for user. Prefer passing `content` over `children` for
   * user/assistant roles; `children` is kept for error footnotes.
   */
  content?: string;
  /** Optional inline meta line (used by tool bubble for "source · duration"). */
  meta?: ReactNode;
  /** Tool bubble header title, e.g. "match_ledger_entries". */
  toolName?: string;
  /** Controls corner-radius continuity when consecutive bubbles share a role. */
  groupPosition?: BubbleGroupPosition;
  /** Show the small "shiftagent" label above assistant content. */
  showAgentLabel?: boolean;
  style?: CSSProperties;
}

export function MessageBubble({
  role,
  timestamp,
  children,
  content,
  meta,
  toolName,
  groupPosition = 'solo',
  showAgentLabel,
  style,
}: Props) {
  const time = shortClockTime(timestamp);

  if (role === 'tool') {
    return (
      <div
        style={{
          background: 'var(--bg-alt2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '10px 12px',
          marginBottom: 10,
          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          fontSize: 12,
          color: 'var(--text-soft)',
          ...style,
        }}
      >
        <div style={{ color: 'var(--accent)', fontWeight: 600, marginBottom: meta ? 2 : 0 }}>
          ⚙ {toolName ?? 'tool'}
        </div>
        {meta && <div>{meta}</div>}
        {children && <div style={{ marginTop: 4 }}>{children}</div>}
      </div>
    );
  }

  const isUser = role === 'user';

  // Parse attachment markers only for user messages — assistant content must never be stripped.
  const parsed = isUser && content ? parseAttachmentMarker(content) : null;
  const displayText = parsed ? parsed.text.replace(/\r\n/g, '\n') : content ?? '';
  const filenames = parsed?.filenames ?? null;

  // The label appears on the first/solo bubble of an assistant group.
  const shouldShowLabel =
    showAgentLabel ?? (!isUser && (groupPosition === 'solo' || groupPosition === 'first'));

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: groupPosition === 'middle' || groupPosition === 'first' ? 3 : 10,
      }}
    >
      <div
        style={{
          maxWidth: isUser ? '78%' : '92%',
          background: isUser ? 'var(--accent-soft)' : 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: bubbleRadius(isUser, groupPosition),
          padding: '10px 14px',
          fontSize: 13,
          color: 'var(--text)',
          lineHeight: 1.5,
          wordWrap: 'break-word',
          position: 'relative',
          overflow: 'hidden',
          ...style,
        }}
      >
        {shouldShowLabel && !isUser && (
          <div
            style={{
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: 0.3,
              color: 'var(--accent)',
              opacity: 0.7,
              marginBottom: 2,
              userSelect: 'none',
            }}
          >
            shiftagent
          </div>
        )}

        {isUser ? (
          <div style={{ whiteSpace: 'pre-wrap' }}>{displayText}</div>
        ) : displayText ? (
          <MarkdownContent content={displayText} />
        ) : null}

        {isUser && filenames && filenames.length > 0 && (
          <div
            style={{
              marginTop: 6,
              paddingTop: 6,
              borderTop: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 6,
              fontSize: 11,
              color: 'var(--text-soft)',
            }}
          >
            <span aria-hidden>📎</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {filenames.map((name, i) => (
                <span
                  key={`${name}-${i}`}
                  style={{
                    display: 'inline-block',
                    padding: '1px 6px',
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 3,
                    fontSize: 10,
                    color: 'var(--text-mute)',
                  }}
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        )}

        {children}

        {time && (
          <div
            style={{
              fontSize: 10,
              color: 'var(--text-mute)',
              marginTop: 6,
              textAlign: isUser ? 'right' : 'left',
              opacity: 0.7,
            }}
          >
            {time}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Radii for a bubble seat within a stack of same-role bubbles. When a bubble is
 * the middle of an assistant run, its vertical seams tighten to ~3px so the
 * consecutive replies read as a connected column rather than floating cards.
 */
function bubbleRadius(isUser: boolean, position: BubbleGroupPosition): string {
  const tight = '3px';
  const normal = '12px';
  const tail = '2px';

  if (isUser) {
    switch (position) {
      case 'first':
        return `${normal} ${normal} ${tight} ${normal}`;
      case 'middle':
        return `${normal} ${tight} ${tight} ${normal}`;
      case 'last':
        return `${normal} ${tight} ${tail} ${normal}`;
      case 'solo':
      default:
        return `${normal} ${normal} ${tail} ${normal}`;
    }
  }
  switch (position) {
    case 'first':
      return `${normal} ${normal} ${normal} ${tight}`;
    case 'middle':
      return `${tight} ${normal} ${normal} ${tight}`;
    case 'last':
      return `${tight} ${normal} ${normal} ${tail}`;
    case 'solo':
    default:
      return `${normal} ${normal} ${normal} ${tail}`;
  }
}
